import { useState, useMemo, useCallback } from 'react';
import { DeliveryNote, Requisition } from '../../types';

/** All list-filter state for the Delivery Notes page */
export function useDeliveryNoteFilters(
  deliveryNotes: DeliveryNote[],
  requisitions: Requisition[],
  getRequisitionInfo: (reqId?: string) => string
) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [vesselFilter, setVesselFilter] = useState<string>('all');
  const [workSiteFilter, setWorkSiteFilter] = useState<string>('all');
  const [requisitionFilter, setRequisitionFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const sameReq = useCallback((reqId: string | undefined, filterVal: string) => {
    if (!reqId) return false;
    const idStr = String(reqId);
    if (idStr === filterVal) return true;
    const req = requisitions.find(
      (r: any) => String(r.documentId || '') === filterVal || String(r.id) === filterVal
    );
    return !!req && (String((req as any).documentId || '') === idStr || String(req.id) === idStr);
  }, [requisitions]);

  const filteredDeliveryNotes = useMemo(() => {
    return deliveryNotes.filter(note => {
      const requisitionInfo = getRequisitionInfo(note.requisitionId);
      const matchesSearch = !searchTerm || (
        (note.deliveryNoteNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (note.vessel || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (note.workSite || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (note.projectLocation || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (note.status || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        requisitionInfo.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || note.status === statusFilter;
      const matchesVessel = vesselFilter === 'all' || (note.vessel || '').toLowerCase().includes(vesselFilter.toLowerCase());
      const matchesWorkSite = workSiteFilter === 'all' || (note.workSite || '').toLowerCase().includes(workSiteFilter.toLowerCase());
      const matchesRequisition = requisitionFilter === 'all' || sameReq(note.requisitionId, requisitionFilter);
      const noteDate = new Date(note.date || note.createdAt);
      const matchesDateFrom = !dateFromFilter || noteDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || noteDate <= new Date(dateToFilter);
      return matchesSearch && matchesStatus && matchesVessel && matchesWorkSite &&
        matchesRequisition && matchesDateFrom && matchesDateTo;
    });
  }, [deliveryNotes, searchTerm, statusFilter, vesselFilter, workSiteFilter, requisitionFilter, dateFromFilter, dateToFilter, getRequisitionInfo, sameReq]);

  const clearFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setVesselFilter('all');
    setWorkSiteFilter('all'); setRequisitionFilter('all'); setDateFromFilter(''); setDateToFilter('');
  };

  const uniqueVessels = Array.from(new Set(deliveryNotes.map(n => n.vessel || '').filter(Boolean)));
  const uniqueWorkSites = Array.from(new Set(deliveryNotes.map(n => n.workSite || '').filter(Boolean)));

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'received', label: 'Received' },
    { value: 'completed', label: 'Completed' }
  ];
  const filterConfigs = [
    { key: 'status', label: 'Status', type: 'select' as const, value: statusFilter, onChange: setStatusFilter, options: statusOptions },
    { key: 'vessel', label: 'Vessel', type: 'select' as const, value: vesselFilter, onChange: setVesselFilter, options: [{ value: 'all', label: 'All Vessels' }, ...uniqueVessels.map(v => ({ value: v, label: v }))] },
    { key: 'workSite', label: 'Work Site', type: 'select' as const, value: workSiteFilter, onChange: setWorkSiteFilter, options: [{ value: 'all', label: 'All Work Sites' }, ...uniqueWorkSites.map(s => ({ value: s, label: s }))] },
    { key: 'requisition', label: 'Requisition', type: 'select' as const, value: requisitionFilter, onChange: setRequisitionFilter, options: [
      { value: 'all', label: 'All Requisitions' },
      ...requisitions.map((req: any) => ({ value: req.documentId || String(req.id), label: `${req.formNumber} - ${req.vessel}` }))
    ] },
    { key: 'dateFrom', label: 'Date From', type: 'date' as const, value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Date To', type: 'date' as const, value: dateToFilter, onChange: setDateToFilter }
  ];

  return { searchTerm, setSearchTerm, filteredDeliveryNotes, clearFilters, filterConfigs };
}
